/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	AIAction,
	useAINudges,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';

import { NudgeContent } from './NudgeContent';

const NUDGE_DISMISS_TIMEOUT = 30000;
const ANIMATION_DURATION = 200;

export const AIProactiveNudge = () => {
	const { changeView } = useViewAIActions();
	const surfaceMetadata = useAISurfaceMetadata();
	const [isNudgeVisible, setIsNudgeVisible] = useState(false);
	const [dismissedIds, setDismissedIds] = useState<string[]>([]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedNudges = useAINudges();

	const [acceptedIds, setAcceptedIds] = useState<string[]>([]);
	const [prevNudge, setPrevNudge] = useState<AIAction | null | undefined>(null);
	const [isMouseHovering, setIsMouseHovering] = useState<boolean>(false);

	const activeNudge = useMemo(
		() =>
			/**
			 * Nudge must not be previously dismissed or accepted
			 * to be considered active.
			 */
			selectedNudges.find(
				(nudge) => !dismissedIds.includes(nudge.id) && !acceptedIds.includes(nudge.id),
			),
		[selectedNudges, dismissedIds, acceptedIds],
	);

	useEffect(() => {
		if (activeNudge !== prevNudge) {
			setPrevNudge(activeNudge);
			if (activeNudge && activeNudge?.id !== prevNudge?.id) {
				const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'shown',
						actionSubject: 'rovoButton',
						actionSubjectId: 'aiRovoProactiveNudge',
						source: surfaceMetadata.contentType,
						attributes: {
							pageMode: surfaceMetadata.pageMode,
							id: activeNudge.id,
							nudgeId,
						},
					},
				}).fire();
				window.requestAnimationFrame(() => {
					/**
					 * Needs to be set to visible in next tick, otherwise
					 * it will not animate
					 */
					setIsNudgeVisible(true);
				});
			}
		}
	}, [activeNudge, prevNudge, createAnalyticsEvent, surfaceMetadata]);

	const onDismissClick = useCallback(() => {
		if (activeNudge) {
			setDismissedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: surfaceMetadata.contentType,
					action: 'dismissed',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						dismissedMethod: 'manual',
						nudgeId,
					},
				},
			}).fire();
		}
	}, [activeNudge, createAnalyticsEvent, surfaceMetadata]);

	const onNudgeClick = useCallback(() => {
		if (activeNudge) {
			const nudgeId = `${activeNudge.title} - ${activeNudge.id}`;
			if (activeNudge.viewConfig) {
				changeView(activeNudge);
			}
			activeNudge.onClick?.();

			setAcceptedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			setDismissedIds((ids) => {
				return [...ids, activeNudge.id];
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: surfaceMetadata.contentType,
					action: 'clicked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoProactiveNudge',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: activeNudge.id,
						nudgeId,
					},
				},
			}).fire();
		}
	}, [activeNudge, changeView, createAnalyticsEvent, surfaceMetadata]);

	const onWrapperMouseEnter = useCallback(() => {
		setIsMouseHovering(true);
	}, []);

	const onWrapperMouseLeave = useCallback(() => {
		setIsMouseHovering(false);
	}, []);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | undefined;
		let slideoutTimeoutId: NodeJS.Timeout | undefined;
		if (activeNudge && !isMouseHovering) {
			timeoutId = setTimeout(() => {
				setDismissedIds((ids) => [...ids, activeNudge.id]);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: surfaceMetadata.contentType,
						action: 'dismissed',
						actionSubject: 'rovoButton',
						actionSubjectId: 'aiRovoProactiveNudge',
						attributes: {
							pageMode: surfaceMetadata.pageMode,
							id: activeNudge.id,
							timerDuration: NUDGE_DISMISS_TIMEOUT,
							dismissedMethod: 'timer_expired',
						},
					},
				}).fire();
			}, NUDGE_DISMISS_TIMEOUT);

			slideoutTimeoutId = setTimeout(() => {
				setIsNudgeVisible(false);
			}, NUDGE_DISMISS_TIMEOUT - ANIMATION_DURATION);
		}

		/**
		 * On mouse enter, isMouseHovering is set to true, will trigger
		 *  cleanup function to clear timeout.
		 */
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (slideoutTimeoutId) {
				clearTimeout(slideoutTimeoutId);
			}
		};
	}, [
		activeNudge,
		createAnalyticsEvent,
		isMouseHovering,
		surfaceMetadata.contentType,
		surfaceMetadata.pageMode,
	]);

	if (!activeNudge) {
		return null;
	}

	return (
		<NudgeContent
			activeNudge={activeNudge}
			isNudgeVisible={isNudgeVisible}
			onWrapperMouseEnter={onWrapperMouseEnter}
			onWrapperMouseLeave={onWrapperMouseLeave}
			onNudgeClick={onNudgeClick}
			onDismissClick={onDismissClick}
		/>
	);
};
